import { useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/ui/Icon";
import { useAuth } from "@/hooks/useAuth";
import { useConfirm } from "@/hooks/useConfirm";
import { useToast } from "@/hooks/useToast";
import { useMyProfile } from "@/hooks/useMyProfile";
import { openMyAccount } from "@/hooks/useMyAccountModal";
import { Avatar } from "./Avatar";
import "./account.css";

/**
 * AccountSheet — A-3 MO 하단 탭 아바타 → 계정 시트(12 설계 §8-A).
 * 옛 동작(아바타 = 즉시 로그아웃 확인)을 대신한다. 로그아웃은 시트 맨 아래, 확인창 kind:"info" 유지.
 * 틀은 메뉴 시트와 같은 `.mob-menu` 바텀시트(components.css, MO 에서만 보임) — 높이만 내용에 맞춘다.
 */
const CLOSE_THRESHOLD = 90;

export function AccountSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const { photo, affiliation } = useMyProfile();
  const [dragY, setDragY] = useState(0);
  const drag = useRef({ startY: 0, active: false });

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    drag.current = { startY: e.clientY, active: true };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }
  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!drag.current.active) return;
    setDragY(Math.max(0, e.clientY - drag.current.startY));
  }
  function onPointerUp() {
    if (!drag.current.active) return;
    drag.current.active = false;
    if (dragY > CLOSE_THRESHOLD) onClose();
    setDragY(0);
  }

  async function handleLogout() {
    onClose();
    if (!(await confirm({ title: "로그아웃할까요?", summary: "다시 사용하려면 로그인해야 합니다.", confirmLabel: "로그아웃", kind: "info" }))) return;
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className={`mob-menu ma-acct-sheet ${open ? "is-open" : ""}`} aria-hidden={!open}>
      <div className="mob-menu-scrim" onClick={onClose} />
      <div
        className="mob-menu-sheet"
        role="dialog"
        aria-label="계정"
        style={open && dragY ? { transform: `translateY(${dragY}px)`, transition: "none" } : undefined}
      >
        <div
          className="mob-menu-grab"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <span className="mob-menu-grab-bar" />
        </div>
        <div className="ma-acct-sheet-body">
          <div className="ma-mo-who">
            <Avatar className="ma-av is-md" photo={photo} name={user?.name} loginId={user?.loginId} />
            <div>
              <div className="ma-who-name">{user?.name || "사용자"}</div>
              <div className="ma-help">{affiliation || user?.loginId}</div>
            </div>
          </div>
          <div className="ma-acct-rows">
            <button
              type="button"
              className="ma-acct-row"
              onClick={() => {
                onClose();
                openMyAccount(null);
              }}
            >
              <Icon name="users" size={18} />
              <span className="ma-acct-row-label">내 계정·환경</span>
              <Icon name="chevR" size={16} />
            </button>
            <button
              type="button"
              className="ma-acct-row"
              onClick={() => {
                onClose();
                toast.info("도움말은 준비 중입니다.");
              }}
            >
              <Icon name="help" size={18} />
              <span className="ma-acct-row-label">도움말</span>
              <Icon name="chevR" size={16} />
            </button>
            {user?.isAdmin && (
              <button
                type="button"
                className="ma-acct-row is-admin"
                onClick={() => {
                  onClose();
                  navigate("/group");
                }}
              >
                <Icon name="shield" size={18} />
                <span className="ma-acct-row-label">통합관리</span>
                <span className="badge ma-badge-admin">관리자</span>
                <Icon name="chevR" size={16} />
              </button>
            )}
            <button type="button" className="ma-acct-row is-danger" onClick={() => void handleLogout()}>
              <Icon name="back" size={18} />
              <span className="ma-acct-row-label">로그아웃</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
